import { useMemo, type Key } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Array as A } from 'effect';
import Box from '@mui/material/Box';
import type { TwoWayMatchTableDragItem } from '@/pages/document/components/DocumentDragAndDrop/dragData';
import {
  twoWayMatchCellsRect,
  type TwoWayMatchCell,
} from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageTwoWayMatchLayer/twoWayMatchGeometry';
import {
  TwoWayMatchTableArea,
  TwoWayMatchTableDragSourceRoot,
} from '@/pages/document/DocumentViewer/DocumentFileView/PageView/PageTwoWayMatchLayer/TwoWayMatchTableDragSource/StyledComponents';

type Props = {
  page: number;
  width: number;
  height: number;
  tableCells: A.NonEmptyReadonlyArray<{ Geometry: { Polygon: TwoWayMatchCell } }>;
  columns: ReadonlyArray<{ id: Key; bindingCell: TwoWayMatchCell }>;
};

const TwoWayMatchTableDragSource = ({ page, width, height, tableCells, columns }: Props) => {
  const item = useMemo<TwoWayMatchTableDragItem>(
    () => ({
      kind: 'twoWayMatchTable',
      page,
      tableCellPolygons: A.map(tableCells, ({ Geometry }) => Geometry.Polygon),
      layerSize: { width, height },
    }),
    [page, tableCells, width, height],
  );
  // The node dnd-kit measures is the table itself, so the drag overlay starts over the table.
  const { setNodeRef, listeners } = useDraggable({ id: `two-way-match-table-${page}`, data: item });

  return (
    <TwoWayMatchTableDragSourceRoot {...listeners}>
      <TwoWayMatchTableArea ref={setNodeRef} style={twoWayMatchCellsRect(item.tableCellPolygons, item.layerSize)} />
      {columns.map(({ bindingCell, id }) => (
        <Box
          key={id}
          data-document-drag-handle
          position="absolute"
          zIndex={100}
          style={{
            cursor: 'move',
            ...twoWayMatchCellsRect([bindingCell], { width, height }),
          }}
        />
      ))}
    </TwoWayMatchTableDragSourceRoot>
  );
};

export default TwoWayMatchTableDragSource;
